import React, { useState, useEffect } from 'react';
import {FileSearch, MessageSquare,ExternalLink, Trash2, AlertTriangle, XCircle} from 'lucide-react';
import useOrdenacaoTabela from '../useOrdenacaoTabela';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// Tipo de cada coluna ordenável (datas e valores precisam de tratamento especial;
// ver useOrdenacaoTabela). Fora do componente para identidade estável no useMemo.
const TIPOS_ORDENACAO = {
    DATA_ESTORNO: 'data',
    DATA_OCORRENCIA: 'data',
    RESTAURANTE: 'texto',
    NOME_CLIENTE: 'texto',
    BANDEIRA_CARTAO: 'texto',
    VALOR_VENDA: 'numero',
    VALOR_ESTORNO: 'numero',
    STATUS: 'texto',
    PROTOCOLO: 'texto',
};

// Estilo do cabeçalho clicável (cursor de mão; não seleciona texto ao clicar).
const thOrdenavel = { padding: '16px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

// Botãozinho que abre o texto escrito pelo Financeiro na coluna Status — serve
// tanto ao motivo da pendência ("Aguardando Retorno da Loja") quanto à
// observação da negativa ("Estorno Negado").
const botaoVerTexto = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    color: '#475569',
    fontSize: '11px',
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: '6px',
    fontWeight: '600',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    textDecoration: 'none'
};

const FinanceiroEstornoControle = ({ statusInicial }) => {
    // --- LÓGICA DE USUÁRIO E TRAVA ---
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // ⚠️ `Operacional` (28/09/2026): acesso TOTAL ao Financeiro (nível Admin) — age
    //    como Admin nas telas do Estorno. Ver `utils/perfisDerivados.js` › ACESSO_TOTAL.
    const podeAlterarStatus = user && ['Admin', 'Financeiro', 'Gerente Adm', 'Operacional'].includes(user.perfil);
    // Só Admin (e o Operacional, nível Admin) pode EXCLUIR lançamentos (ação destrutiva e definitiva).
    const isAdmin = user && (user.perfil === 'Admin' || user.perfil === 'Operacional');
    // Apenas estes perfis podem ver os lançamentos de TODAS as lojas.
    // Qualquer outro perfil (inclusive Gerente / Gerente Plantao) fica travado na própria loja.
    // 'Auditoria' entra na visão total como SOMENTE LEITURA (auditor precisa ver a rede toda;
    // as ações de escrita continuam atrás de podeAlterarStatus/isAdmin, que não o incluem).
    const PERFIS_VISAO_TOTAL = ['Admin', 'Financeiro', 'Gerente Adm', 'Diretoria', 'Auditoria', 'Operacional'];
    const podeVerTodasLojas = user && PERFIS_VISAO_TOTAL.includes(perfilNoModulo(user.perfil, 'financeiro'));

    // --- ESTADOS ---
    const [dadosEstornos, setDadosEstornos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [filtroLoja, setFiltroLoja] = useState(podeVerTodasLojas ? 'todas' : user.loja);
    // statusInicial vem do deep-link da Home (ex.: 'Aguardando Retorno da Loja').
    const [filtroStatus, setFiltroStatus] = useState(statusInicial || 'todos');

    const hoje = new Date();

    const [filtroPeriodo, setFiltroPeriodo] = useState({
      mes: String(hoje.getMonth() + 1).padStart(2, '0'),
      ano: String(hoje.getFullYear())
    });
    
    const meses = [
      { valor: '01', nome: 'Janeiro' },
      { valor: '02', nome: 'Fevereiro' },
      { valor: '03', nome: 'Março' },
      { valor: '04', nome: 'Abril' },
      { valor: '05', nome: 'Maio' },
      { valor: '06', nome: 'Junho' },
      { valor: '07', nome: 'Julho' },
      { valor: '08', nome: 'Agosto' },
      { valor: '09', nome: 'Setembro' },
      { valor: '10', nome: 'Outubro' },
      { valor: '11', nome: 'Novembro' },
      { valor: '12', nome: 'Dezembro' }
    ];

    const anos = [];

    for (let i = 2024; i <= 2030; i++) {
      anos.push(String(i));
    }

    // Modais
    const [modalAberto, setModalAberto] = useState(false);
    const [statusPendente, setStatusPendente] = useState('');
    const [motivoTexto, setMotivoTexto] = useState('');
    const [modalLeituraAberto, setModalLeituraAberto] = useState(false);
    const [motivoSelecionado, setMotivoSelecionado] = useState('');
    // O modal de leitura serve a dois textos diferentes (motivo da pendência com a
    // loja e observação da negativa) — o título muda conforme quem abriu.
    const [tituloLeitura, setTituloLeitura] = useState('Motivo da Pendência');

    // Modal "Estorno Negado" — observação obrigatória com o motivo da negativa
    const [modalNegadoAberto, setModalNegadoAberto] = useState(false);
    const [motivoNegadoTexto, setMotivoNegadoTexto] = useState('');

    // Modal Estorno Efetuado (protocolo + data do estorno)
    const [modalProtocoloAberto, setModalProtocoloAberto] = useState(false);
    const [protocoloTexto, setProtocoloTexto] = useState('');
    const [dataEstornoTexto, setDataEstornoTexto] = useState('');

    // Modal "Ver solicitação" — mostra tudo que foi preenchido no envio
    const [modalVerAberto, setModalVerAberto] = useState(false);
    const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);

    // Modal de exclusão (Admin) — guarda o lançamento a excluir e o estado de envio
    const [itemParaExcluir, setItemParaExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    const abrirModalVer = (item) => {
        setSolicitacaoSelecionada(item);
        setModalVerAberto(true);
    };

    const abrirModalLeitura = (motivo, titulo = 'Motivo da Pendência') => {
        setMotivoSelecionado(motivo || 'Nenhum motivo detalhado foi informado pelo Financeiro.');
        setTituloLeitura(titulo);
        setModalLeituraAberto(true);
    };
    
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');

    const carregarEstornos = async () => {
        setLoading(true);
        try {
            const url = `/api/estorno/listar?loja=${filtroLoja}&status=${filtroStatus}&periodo=${filtroPeriodo.ano}-${filtroPeriodo.mes}`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const data = await res.json();
                setDadosEstornos(data.estornos || []);
            }
        } catch (err) { console.error('Erro de conexão', err); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        // Trocar de filtro muda a lista na tela: a seleção anterior é descartada
        // para não aplicar status em solicitações que não estão mais visíveis.
        setSelectedIds([]);
        carregarEstornos();
        }, [filtroLoja, filtroStatus, filtroPeriodo]);

    // Exclui (definitivamente) o lançamento selecionado no modal. Só Admin chega aqui.
    const confirmarExclusao = async () => {
        if (!itemParaExcluir) return;
        setExcluindo(true);
        try {
            const res = await fetch(`/api/estorno/${itemParaExcluir.ID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setItemParaExcluir(null);
                carregarEstornos();
            } else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || 'Erro ao excluir o lançamento.');
            }
        } catch (err) {
            alert('Erro de conexão com o servidor.');
        } finally {
            setExcluindo(false);
        }
    };

    // Ordenação clicável pelo cabeçalho da tabela (ver useOrdenacaoTabela).
    const { dadosOrdenados, ordenar, indicador } = useOrdenacaoTabela(dadosEstornos, TIPOS_ORDENACAO);

    // Lógica de Seleção
    const handleSelectAll = (e) => setSelectedIds(e.target.checked ? dadosEstornos.map(i => i.ID) : []);
    const handleSelectRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

    // Função de Aprovação/Alteração
    const executarAtualizacaoStatus = async (novoStatus, motivo = '', protocolo = '', dataEstorno = '', motivoNegado = '') => {
        const quantidade = selectedIds.length;

        // Status que já passam por um modal próprio (com campos a preencher) não
        // pedem a confirmação padrão — o próprio modal é a confirmação.
        const STATUS_COM_MODAL = ['Aguardando Retorno da Loja', 'Estorno Efetuado', 'Estorno Negado'];
        if (!STATUS_COM_MODAL.includes(novoStatus)) {
        const confirmou = window.confirm(`Tem certeza que deseja alterar o status de ${quantidade} solicitação(ões) para "${novoStatus}"?`);
        if (!confirmou) return;
        }

        try {
        const token = localStorage.getItem('authToken');
        const res = await fetch('/api/estorno/atualizar-status', {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                ids: selectedIds,
                status: novoStatus,
                motivo: motivo,
                motivoNegado: motivoNegado,
                protocolo: protocolo,
                dataEstorno: dataEstorno
            })
        });

        if (res.ok) {
            alert('Status atualizado com sucesso!');
            // A alteração já foi aplicada: desmarca as linhas para não sobrar
            // seleção "velha" na tela (senão o próximo status escolhido cairia
            // de novo sobre as mesmas solicitações, sem o usuário perceber).
            setSelectedIds([]);
            setModalAberto(false);
            setModalProtocoloAberto(false);
            setModalNegadoAberto(false);
            setMotivoTexto('');
            setMotivoNegadoTexto('');
            setProtocoloTexto('');
            setDataEstornoTexto('');
            carregarEstornos();
        } else {
            const data = await res.json().catch(() => ({}));
            alert(data.error || 'Erro ao atualizar status.');
        }
        } catch (err) {
        alert('Erro de conexão com o servidor.');
        }
    };

    // Enviar alteração de status em lote para API com Pop-up de Confirmação
    const handleStatusChangeEmLote = (novoStatus) => {
        if (selectedIds.length === 0 || !novoStatus) return;
        
        if (novoStatus === 'Aguardando Retorno da Loja') {
        setStatusPendente(novoStatus);
        setModalAberto(true);
        } else if (novoStatus === 'Estorno Efetuado') {
        setModalProtocoloAberto(true);
        } else if (novoStatus === 'Estorno Negado') {
        setModalNegadoAberto(true);
        } else {
        executarAtualizacaoStatus(novoStatus);
        }
    };

    // Função acionada ao clicar em Salvar no Modal
    const confirmarStatusComMotivo = () => {
        if (!motivoTexto.trim()) {
            alert('Por favor, insira o motivo para a loja.');
            return;
        }
        executarAtualizacaoStatus(statusPendente, motivoTexto);
    };

    // Negar o estorno exige a observação — sem ela não sobra registro do porquê
    // da decisão (o backend também barra, esta é só a validação amigável).
    const confirmarNegativa = () => {
        if (!motivoNegadoTexto.trim()) {
            alert('Por favor, informe a observação com o motivo da negativa.');
            return;
        }
        executarAtualizacaoStatus('Estorno Negado', '', '', '', motivoNegadoTexto);
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Aguardando Retorno da Loja': return { bg: '#eff6ff', text: '#1d4ed8' }; 
            case 'Estorno Efetuado': return { bg: '#f0fdf4', text: '#15803d' }; 
            case 'Estorno Negado': return { bg: '#fef2f2', text: '#b91c1c' }; 
            default: return { bg: '#fff7ed', text: '#c2410c' }; 
        }
    };

    return (
        <div>
            {/* FILTROS E AÇÕES EM LOTE */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px', backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', alignItems: 'center' }}>
                <select value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)} disabled={!podeVerTodasLojas} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    <option value="todas">Todas as Lojas</option>
                    {['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <select
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value)}
                    style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1'
                    }}
                    >
                    <option value="todos">Todos os Status</option>
                    <option value="Aguardando Retorno do Financeiro">
                        Aguardando Retorno do Financeiro
                    </option>
                    <option value="Aguardando Retorno da Loja">
                        Aguardando Retorno da Loja
                    </option>
                    <option value="Estorno Efetuado">Estorno Efetuado</option>
                    <option value="Estorno Negado">Estorno Negado</option>
                    </select>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>

                      <select
                        value={filtroPeriodo.mes}
                        onChange={(e) =>
                          setFiltroPeriodo(prev => ({
                            ...prev,
                            mes: e.target.value
                          }))
                        }
                        style={{
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1'
                        }}
                      >
                        {meses.map((mes) => (
                          <option key={mes.valor} value={mes.valor}>
                            {mes.nome}
                          </option>
                        ))}
                      </select>

                      <select
                        value={filtroPeriodo.ano}
                        onChange={(e) =>
                          setFiltroPeriodo(prev => ({
                            ...prev,
                            ano: e.target.value
                          }))
                        }
                        style={{
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1'
                        }}
                      >
                        {anos.map((ano) => (
                          <option key={ano} value={ano}>
                            {ano}
                          </option>
                        ))}
                      </select>

                    </div>
                {podeAlterarStatus && selectedIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', backgroundColor: '#f0fdfa', border: '1px solid #ccfbf1', padding: '15px 20px', borderRadius: '12px', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#115e59', fontWeight: '600', fontSize: '14px' }}>
                    {selectedIds.length} {selectedIds.length === 1 ? 'solicitação selecionada' : 'solicitações selecionadas'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Alterar status para:</label>
                    <select 
                      defaultValue="" 
                      onChange={(e) => {
                        handleStatusChangeEmLote(e.target.value);
                        e.target.value = ""; 
                      }} 
                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #94a3b8', fontSize: '14px', backgroundColor: '#fff', fontWeight: '500' }}
                    >
                      <option value="" disabled>Selecione uma opção...</option>
                      <option value="Aguardando Retorno do Financeiro">Aguardando Retorno do Financeiro</option>
                      <option value="Aguardando Retorno da Loja">Aguardando Retorno da Loja</option>
                      <option value="Estorno Efetuado">Estorno Efetuado</option>
                      <option value="Estorno Negado">Estorno Negado</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* TABELA */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8fafc', fontSize: '12px', color: '#64748b' }}>
                        <tr>
                            {/* Coluna de seleção só existe pra quem pode alterar status — o <td> por
                                linha já era condicional; sem esta trava o cabeçalho desalinhava. */}
                            {podeAlterarStatus && (
                                <th style={{ padding: '16px' }}><input type="checkbox" onChange={handleSelectAll} checked={selectedIds.length === dadosEstornos.length && dadosEstornos.length > 0} /></th>
                            )}
                            <th onClick={() => ordenar('DATA_ESTORNO')} style={thOrdenavel}>Data do Estorno{indicador('DATA_ESTORNO')}</th>
                            <th onClick={() => ordenar('DATA_OCORRENCIA')} style={thOrdenavel}>Data da venda{indicador('DATA_OCORRENCIA')}</th>
                            <th onClick={() => ordenar('RESTAURANTE')} style={thOrdenavel}>Loja{indicador('RESTAURANTE')}</th>
                            <th onClick={() => ordenar('NOME_CLIENTE')} style={thOrdenavel}>Nome{indicador('NOME_CLIENTE')}</th>
                            <th onClick={() => ordenar('BANDEIRA_CARTAO')} style={thOrdenavel}>Forma de Pagamento{indicador('BANDEIRA_CARTAO')}</th>
                            <th onClick={() => ordenar('VALOR_VENDA')} style={thOrdenavel}>Valor Total{indicador('VALOR_VENDA')}</th>
                            <th onClick={() => ordenar('VALOR_ESTORNO')} style={thOrdenavel}>Valor Estorno{indicador('VALOR_ESTORNO')}</th>
                            <th onClick={() => ordenar('STATUS')} style={thOrdenavel}>Status{indicador('STATUS')}</th>
                            <th onClick={() => ordenar('PROTOCOLO')} style={thOrdenavel}>Protocolo{indicador('PROTOCOLO')}</th>
                            <th style={{ padding: '16px' }}>Ação</th>
                        </tr>
                    </thead>
                    <tbody>
                      {dadosOrdenados.map((item) => {
                        const styleStatus = getStatusStyle(item.STATUS);
                        return (
                          <tr key={item.ID} style={{ borderBottom: '1px solid #edf2f7' }}>
                            {podeAlterarStatus && (
                              <td style={{ padding: '14px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedIds.includes(item.ID)} 
                                  onChange={() => handleSelectRow(item.ID)}
                                  style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                                />
                              </td>
                            )}
                            <td style={{ padding: '14px', textAlign: 'center', color: '#334155' }}>
                              {item.DATA_ESTORNO || <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>}
                            </td>
                            <td style={{ padding: '14px', color: '#334155' }}>{item.DATA_OCORRENCIA}</td>
                            <td style={{ padding: '14px', color: '#334155' }}>{item.RESTAURANTE}</td>
                            <td style={{ padding: '14px', color: '#334155', fontWeight: '500' }}>{item.NOME_CLIENTE}</td>
                            <td style={{ padding: '14px', color: '#334155' }}>
                              {item.BANDEIRA_CARTAO} {item.TIPO_CARTAO ? `(${item.TIPO_CARTAO})` : ''}
                            </td>
                            <td style={{ padding: '14px', color: '#334155' }}>R$ {Number(item.VALOR_VENDA).toFixed(2)}</td>
                            <td style={{ padding: '14px', color: '#e11d48', fontWeight: '600' }}>R$ {Number(item.VALOR_ESTORNO).toFixed(2)}</td>
                            <td style={{ padding: '14px' }}>
                              <span style={{ 
                                backgroundColor: styleStatus.bg, 
                                color: styleStatus.text, 
                                padding: '6px 12px', 
                                borderRadius: '50px', 
                                fontSize: '12px', 
                                fontWeight: '700',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.STATUS || 'Aguardando Retorno do Financeiro'}
                              </span>
                              {item.STATUS === 'Aguardando Retorno da Loja' && (
                                <div style={{ marginTop: '6px', textAlign: 'center' }}>
                                  <button
                                    onClick={() => abrirModalLeitura(item.MOTIVO_RETORNO)}
                                    title="Ler motivo da pendência"
                                    style={botaoVerTexto}
                                  >
                                    <MessageSquare size={12} color="#64748b" />
                                    Ver motivo
                                  </button>
                                </div>
                              )}
                              {item.STATUS === 'Estorno Negado' && (
                                <div style={{ marginTop: '6px', textAlign: 'center' }}>
                                  <button
                                    onClick={() => abrirModalLeitura(item.MOTIVO_NEGADO, 'Motivo da Negativa')}
                                    title="Ler a observação do Financeiro sobre a negativa"
                                    style={botaoVerTexto}
                                  >
                                    <MessageSquare size={12} color="#64748b" />
                                    Ver observação
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'center' }}>
                              {item.PROTOCOLO ? (
                                <span style={{
                                  display: 'inline-block',
                                  backgroundColor: '#f0fdf4',
                                  color: '#15803d',
                                  border: '1px solid #bbf7d0',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.5px'
                                }}>
                                  {item.PROTOCOLO}
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                <button
                                  onClick={() => abrirModalVer(item)}
                                  title="Ver dados da solicitação"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', fontSize: '11px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontWeight: '600', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                                >
                                  <FileSearch size={12} color="#64748b" />
                                  Ver solicitação
                                </button>
                                {item.COMPROVANTE_URL ? (
                                  <a href={item.COMPROVANTE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#0d9488', textDecoration: 'none', fontWeight: '600', fontSize: '13px' }}>
                                    Comprovante <ExternalLink size={14} />
                                  </a>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: '13px' }}>Sem anexo</span>
                                )}
                                {isAdmin && (
                                  <button
                                    onClick={() => setItemParaExcluir(item)}
                                    title="Excluir lançamento"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: '#fff', border: '1px solid #fecaca', color: '#dc2626', fontSize: '11px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}
                                  >
                                    <Trash2 size={12} /> Excluir
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                        {podeAlterarStatus && <td style={{ padding: '14px' }} />}
                        <td colSpan={5} style={{ padding: '14px', fontWeight: '700', color: '#334155', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Total ({dadosEstornos.length} {dadosEstornos.length === 1 ? 'registro' : 'registros'})
                        </td>
                        <td style={{ padding: '14px', fontWeight: '700', color: '#334155', fontSize: '14px' }}>
                          R$ {dadosEstornos.reduce((acc, item) => acc + Number(item.VALOR_VENDA || 0), 0).toFixed(2)}
                        </td>
                        <td style={{ padding: '14px', fontWeight: '700', color: '#e11d48', fontSize: '14px' }}>
                          R$ {dadosEstornos.reduce((acc, item) => acc + Number(item.VALOR_ESTORNO || 0), 0).toFixed(2)}
                        </td>
                        <td colSpan={3} style={{ padding: '14px' }} />
                      </tr>
                    </tfoot>
                </table>
            </div>
            {modalAberto && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                    <h3 style={{ marginTop: 0, color: '#0f766e', fontSize: '20px' }}>Informar Motivo para a Loja</h3>
                    <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
                        Descreva o motivo pelo qual o estorno não pôde ser realizado para auxiliar a loja. 
                        Isso afetará {selectedIds.length} solicitação(ões).
                    </p>
                    
                    <textarea 
                        value={motivoTexto}
                        onChange={(e) => setMotivoTexto(e.target.value)}
                        placeholder="Ex: Faltou o comprovante legível da máquina..."
                        rows={4}
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', marginBottom: '20px', resize: 'vertical', fontFamily: 'inherit' }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button 
                        onClick={() => {
                            setModalAberto(false);
                            setMotivoTexto('');
                        }}
                        style={{ padding: '10px 16px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                        Cancelar
                        </button>
                        <button 
                        onClick={confirmarStatusComMotivo}
                        style={{ padding: '10px 16px', backgroundColor: '#0d9488', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                        Confirmar e Salvar
                        </button>
                    </div>
                    </div>
                </div>
                )}
            {/* MODAL: Protocolo GETNET para Estorno Efetuado */}
            {modalProtocoloAberto && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: '18px' }}>✅</span>
                            </div>
                            <h3 style={{ margin: 0, color: '#15803d', fontSize: '18px', fontWeight: '700' }}>Estorno Efetuado</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 20px 0', lineHeight: '1.5' }}>
                            Informe a <strong>data em que o estorno foi realizado</strong> e o <strong>número do protocolo</strong> referente a este estorno.
                            Isso afetará <strong>{selectedIds.length}</strong> solicitação(ões).
                        </p>

                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Data do Estorno <span style={{ color: '#dc2626', fontWeight: '400' }}>*</span>
                        </label>
                        <input
                            type="date"
                            value={dataEstornoTexto}
                            onChange={(e) => setDataEstornoTexto(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                fontSize: '14px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                marginBottom: '18px',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#15803d'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />

                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Nº Protocolo <span style={{ color: '#94a3b8', fontWeight: '400' }}>(opcional)</span>
                        </label>
                        <input
                            type="text"
                            value={protocoloTexto}
                            onChange={(e) => setProtocoloTexto(e.target.value)}
                            placeholder="Ex: 202605250001234"
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                letterSpacing: '0.5px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                marginBottom: '24px',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#15803d'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    setModalProtocoloAberto(false);
                                    setProtocoloTexto('');
                                    setDataEstornoTexto('');
                                }}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    if (!dataEstornoTexto) {
                                        alert('Informe a data em que o estorno foi realizado.');
                                        return;
                                    }
                                    executarAtualizacaoStatus('Estorno Efetuado', '', protocoloTexto, dataEstornoTexto);
                                }}
                                style={{ padding: '10px 18px', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                ✅ Confirmar Estorno
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* MODAL: Observação obrigatória ao marcar "Estorno Negado" */}
            {modalNegadoAberto && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '14px', width: '90%', maxWidth: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <XCircle size={18} color="#b91c1c" />
                            </div>
                            <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '18px', fontWeight: '700' }}>Estorno Negado</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 20px 0', lineHeight: '1.5' }}>
                            Descreva o <strong>motivo da negativa</strong>. O registro é interno (não é enviado ao cliente)
                            e fica disponível para a loja e a Auditoria consultarem depois.
                            Isso afetará <strong>{selectedIds.length}</strong> solicitação(ões).
                        </p>

                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Observação <span style={{ color: '#dc2626', fontWeight: '400' }}>*</span>
                        </label>
                        <textarea
                            value={motivoNegadoTexto}
                            onChange={(e) => setMotivoNegadoTexto(e.target.value)}
                            placeholder="Ex: Venda localizada e conferida na maquineta — não houve cobrança em duplicidade."
                            rows={4}
                            style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '24px', resize: 'vertical', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
                            onFocus={(e) => e.target.style.borderColor = '#b91c1c'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    setModalNegadoAberto(false);
                                    setMotivoNegadoTexto('');
                                }}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarNegativa}
                                style={{ padding: '10px 18px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <XCircle size={15} /> Confirmar Negativa
                            </button>
                        </div>
                    </div>
                </div>
            )}
                {modalLeituraAberto && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                    
                    <h3 style={{ marginTop: 0, color: '#b91c1c', fontSize: '18px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                        {tituloLeitura}
                    </h3>
                    
                    <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '15px', marginBottom: '25px', minHeight: '80px' }}>
                        <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {motivoSelecionado}
                        </p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button 
                        onClick={() => setModalLeituraAberto(false)}
                        style={{ padding: '8px 20px', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                        Fechar
                        </button>
                    </div>
                    </div>
                </div>
                )}
            {/* MODAL: Ver dados da solicitação de estorno */}
            {modalVerAberto && solicitacaoSelecionada && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                        <h3 style={{ marginTop: 0, color: '#0f766e', fontSize: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                            Dados da Solicitação
                        </h3>

                        {(() => {
                            const s = solicitacaoSelecionada;
                            const linhas = [
                                ['Loja', s.RESTAURANTE],
                                ['Data da Solicitação', s.DATA_SOLICITACAO],
                                ['Data da Venda', s.DATA_OCORRENCIA],
                                ['Nome do Cliente', s.NOME_CLIENTE],
                                ['Telefone', s.TELEFONE_CLIENTE],
                                ['E-mail', s.EMAIL_CLIENTE],
                                ['Forma de Pagamento', `${s.BANDEIRA_CARTAO || '—'}${s.TIPO_CARTAO ? ` (${s.TIPO_CARTAO})` : ''}`],
                                ['Nº do Cartão', s.NUMERO_CARTAO],
                                ['Chave PIX', s.CHAVE_PIX],
                                ['Valor da Venda', s.VALOR_VENDA != null ? `R$ ${Number(s.VALOR_VENDA).toFixed(2)}` : null],
                                ['Valor do Estorno', s.VALOR_ESTORNO != null ? `R$ ${Number(s.VALOR_ESTORNO).toFixed(2)}` : null],
                                ['Descrição da Ocorrência', s.DESCRICAO_OCORRENCIA]
                            ];
                            return (
                                <div style={{ marginTop: '16px' }}>
                                    {linhas.map(([rotulo, valor]) => (
                                        <div key={rotulo} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>{rotulo}</span>
                                            <span style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {valor != null && valor !== '' ? valor : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button
                                onClick={() => { setModalVerAberto(false); setSolicitacaoSelecionada(null); }}
                                style={{ padding: '8px 20px', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* MODAL: Confirmar exclusão (Admin) */}
            {itemParaExcluir && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Trash2 size={18} color="#dc2626" />
                            </div>
                            <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '18px', fontWeight: '700' }}>Excluir lançamento</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                            Esta ação é <strong>definitiva</strong> e não pode ser desfeita. Confirme os dados do lançamento:
                        </p>
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '14px', color: '#334155' }}>
                            <div><strong>Loja:</strong> {itemParaExcluir.RESTAURANTE}</div>
                            <div><strong>Cliente:</strong> {itemParaExcluir.NOME_CLIENTE}</div>
                            <div><strong>Data da venda:</strong> {itemParaExcluir.DATA_OCORRENCIA}</div>
                            <div><strong>Valor do estorno:</strong> R$ {Number(itemParaExcluir.VALOR_ESTORNO).toFixed(2)}</div>
                        </div>
                        {itemParaExcluir.STATUS === 'Estorno Efetuado' && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px' }}>
                                <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span style={{ fontSize: '13px', color: '#92400e' }}>
                                    Atenção: este estorno já consta como <strong>efetuado</strong>. Excluí-lo apaga o registro do estorno realizado.
                                </span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() => setItemParaExcluir(null)}
                                disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarExclusao}
                                disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: excluindo ? '#fca5a5' : '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: excluindo ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <Trash2 size={15} /> {excluindo ? 'Excluindo...' : 'Excluir definitivamente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default FinanceiroEstornoControle;